import { useState } from 'react';
import { Search, AlertCircle, CheckCircle, Info, Trash2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useLanguage } from '../contexts/LanguageContext';
import { useModal } from '../contexts/ModalContext';
import { log } from '../lib/logger';

interface UserStatus {
  email: string;
  timestamp: string;
  exists_in_auth: boolean;
  exists_in_roles: boolean;
  has_pending_invitation: boolean;
  status: string;
  can_invite: boolean;
  recommended_action: string;
  auth_user?: any;
  role?: any;
  invitation?: any;
}

export function UserStatusChecker() {
  const { t, translateError } = useLanguage();
  const { showConfirm, toast } = useModal();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<UserStatus | null>(null);
  const [error, setError] = useState<string | null>(null);

  const checkStatus = async () => {
    if (!email.trim()) return;

    setLoading(true);
    setError(null);
    setStatus(null);

    try {
      const { data, error: rpcError } = await supabase.rpc('admin_check_user_status', {
        check_email: email.trim().toLowerCase()
      });

      if (rpcError) {
        throw rpcError;
      }

      setStatus(data);
    } catch (err: any) {
      log.error('Error checking user status:', err);
      setError(translateError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleCleanup = async () => {
    if (!email.trim() || !status) return;

    const confirmed = await showConfirm({
      title: 'Clean Up User Data?',
      message: `Remove all orphaned authentication and invitation data for ${email}? This action cannot be undone.`,
      confirmText: 'Clean Up',
      cancelText: 'Cancel',
      variant: 'warning',
    });

    if (!confirmed) return;

    setLoading(true);
    try {
      const { data, error: cleanupError } = await supabase.functions.invoke('cleanup-orphaned-user', {
        body: { email: email.trim().toLowerCase() }
      });

      if (cleanupError) {
        throw cleanupError;
      }

      if (data.success) {
        toast.success(t('admin.cleanupSuccess'));
        setEmail('');
        setStatus(null);
      } else {
        throw new Error(data.message || 'Cleanup failed');
      }
    } catch (err: any) {
      log.error('Error cleaning up user:', err);
      toast.error(t('admin.cleanupFailed') + ': ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (statusType: string) => {
    switch (statusType) {
      case 'ACTIVE_USER':
        return {
          bg: 'bg-green-50',
          border: 'border-green-200',
          text: 'text-green-800',
          icon: CheckCircle,
          iconColor: 'text-green-600'
        };
      case 'ORPHANED_AUTH_USER':
      case 'CONFIRMED_NO_ROLE':
        return {
          bg: 'bg-amber-50',
          border: 'border-amber-200',
          text: 'text-amber-800',
          icon: AlertCircle,
          iconColor: 'text-amber-600'
        };
      case 'PENDING_INVITATION':
        return {
          bg: 'bg-blue-50',
          border: 'border-blue-200',
          text: 'text-blue-800',
          icon: Info,
          iconColor: 'text-blue-600'
        };
      default:
        return {
          bg: 'bg-gray-50',
          border: 'border-gray-200',
          text: 'text-gray-800',
          icon: Info,
          iconColor: 'text-gray-600'
        };
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-100 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">
        {t('admin.checkUserStatus')}
      </h3>
      <p className="text-sm text-gray-600 mb-4">
        {t('admin.checkUserStatusDesc')}
      </p>

      <div className="flex gap-2 mb-6">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && checkStatus()}
          placeholder={t('admin.emailPlaceholder')}
          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
          disabled={loading}
        />
        <button
          onClick={checkStatus}
          disabled={loading || !email.trim()}
          className="flex items-center gap-2 px-4 py-2 bg-accent-600 text-white rounded-lg hover:bg-accent-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Search className="w-5 h-5" />
          {loading ? t('common.loading') + '...' : t('admin.checkStatus')}
        </button>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {status && (
        <div className={`p-6 ${getStatusColor(status.status).bg} border ${getStatusColor(status.status).border} rounded-lg`}>
          <div className="flex items-start gap-3 mb-4">
            {(() => {
              const { icon: Icon, iconColor } = getStatusColor(status.status);
              return <Icon className={`w-6 h-6 ${iconColor} mt-0.5`} />;
            })()}
            <div className="flex-1">
              <h4 className={`text-lg font-semibold ${getStatusColor(status.status).text} mb-2`}>
                {status.status.replace(/_/g, ' ')}
              </h4>
              <p className={`text-sm ${getStatusColor(status.status).text} mb-4`}>
                {status.recommended_action}
              </p>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-xs font-semibold text-gray-600 mb-1">{t('admin.existsInAuth')}</p>
                  <p className={`text-sm font-medium ${status.exists_in_auth ? 'text-green-700' : 'text-gray-500'}`}>
                    {status.exists_in_auth ? t('common.yes') : t('common.no')}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-600 mb-1">{t('admin.hasRole')}</p>
                  <p className={`text-sm font-medium ${status.exists_in_roles ? 'text-green-700' : 'text-gray-500'}`}>
                    {status.exists_in_roles ? t('common.yes') : t('common.no')}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-600 mb-1">{t('admin.hasPendingInvite')}</p>
                  <p className={`text-sm font-medium ${status.has_pending_invitation ? 'text-blue-700' : 'text-gray-500'}`}>
                    {status.has_pending_invitation ? t('common.yes') : t('common.no')}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-600 mb-1">{t('admin.canSendInvite')}</p>
                  <p className={`text-sm font-medium ${status.can_invite ? 'text-green-700' : 'text-red-700'}`}>
                    {status.can_invite ? t('common.yes') : t('common.no')}
                  </p>
                </div>
              </div>

              {status.status === 'ORPHANED_AUTH_USER' && (
                <button
                  onClick={handleCleanup}
                  disabled={loading}
                  className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  {t('admin.cleanupOrphanedUser')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
